import React, { useCallback, useMemo } from 'react';
import SearchIcon from '@material-ui/icons/Search';
import type { AutocompleteInputChangeReason } from '@material-ui/lab/useAutocomplete/useAutocomplete';
import { AutocompleteInput } from '@this/shared/ui/inputs/autocomplete';
import { Text } from '@this/shared/ui/data_displays/typography';
import { Link } from '@this/src/components/shared/ui/navigations/link';
import type { AITTheme } from '@this/shared/ui/theme';
import { useAITTheme } from '@this/shared/ui/theme';
import type HotelList from '@this/domain/hotel/hotel_list';
import { splitNameKeywords } from '@this/domain/hotel/hotel_name_filter';
import { formatPrice } from '@this/src/util';
import { HotelNameHighlight } from './hotel_name_highlight';

type Props = {
  hotels: HotelList;
  isPcLayout?: boolean;
};

type Hotel = HotelList['list'][number];

const getOptionLabel = (option: string | Hotel): string =>
  typeof option === 'string' ? option : option.name ?? '';

const optionCaption = (hotel: Hotel): string => {
  const averagePrice = hotel.getAveragePrice();
  const price = averagePrice == null ? null : `${formatPrice(averagePrice)}/泊`;
  return [hotel.addressText(), price].filter(Boolean).join('・');
};

const ListAreaHotelNameSearch: React.FC<Props> = ({ hotels, isPcLayout = false }) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme, isPcLayout), [theme, isPcLayout]);
  const keyword = hotels.filter.nameKeyword;

  const handleInputChange = useCallback(
    (_event: React.ChangeEvent<unknown>, value: string, reason: AutocompleteInputChangeReason) => {
      // 'reset' はサジェスト選択時や inputValue 追従時に空文字で発火しキーワードを消してしまうため、選択は onChange 側で扱う
      if (reason === 'input' || reason === 'clear') {
        hotels.setNameKeyword(value);
      }
    },
    [hotels]
  );

  const handleChange = useCallback(
    (_event: React.ChangeEvent<unknown>, value: string | Hotel | null) => {
      hotels.setNameKeyword(typeof value === 'string' ? value : value?.name ?? '');
    },
    [hotels]
  );

  const renderOption = useCallback(
    (hotel: Hotel) => {
      const caption = optionCaption(hotel);
      return (
        <div style={styles.option}>
          <div style={styles.optionName}>
            <HotelNameHighlight text={hotel.name ?? ''} keyword={keyword} />
          </div>
          {caption && (
            <Text color="description" level="caption">
              {caption}
            </Text>
          )}
        </div>
      );
    },
    [styles, keyword]
  );

  return (
    <div style={styles.container}>
      <div style={styles.labelRow}>
        <SearchIcon style={styles.searchIcon} />
        <span style={styles.label}>この検索結果をホテル名で絞り込む</span>
        <Link size="small" onClick={() => hotels.setNameKeyword('')}>
          クリア
        </Link>
      </div>
      <AutocompleteInput<Hotel>
        async="NONE"
        size="small"
        style={{ width: '100%' }}
        options={hotels.list}
        filterOptions={(_options, state) => hotels.suggestByName(state.inputValue)}
        inputValue={keyword}
        onInputChange={handleInputChange}
        onChange={handleChange}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
      />
      {splitNameKeywords(keyword).length > 0 && (
        <div style={styles.caption}>
          <Text color="description" level="caption">
            「<span data-wovn-ignore>{keyword}</span>」に一致する候補
            {hotels.filterLength === 0 ? (
              'はありません'
            ) : (
              <>
                ：<span data-wovn-ignore>{hotels.filterLength}</span>件
              </>
            )}
          </Text>
        </div>
      )}
    </div>
  );
};

const getStyles = (theme: AITTheme, isPcLayout: boolean) => ({
  container: {
    marginBottom: 10,
    flexShrink: isPcLayout ? 0 : undefined
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginBottom: 4
  },
  searchIcon: {
    fontSize: 14,
    color: theme.grayTextColor
  },
  label: {
    fontSize: 11,
    fontWeight: 'bold' as const,
    marginRight: 'auto' as const
  },
  option: {
    display: 'flex',
    flexDirection: 'column' as const,
    width: '100%'
  },
  optionName: {
    fontSize: 12
  },
  caption: {
    marginTop: 4
  }
});

export default ListAreaHotelNameSearch;
